import React, { useState, useEffect } from 'react';
import { FileCheck, Filter, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';

import hrDocumentApprovalService from '../../services/hrDocumentApprovalService';

import { useAuth } from '../../contexts/AuthContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import DocumentApprovalCard from './components/DocumentApprovalCard';
import DocumentReviewModal from './components/DocumentReviewModal';
import ApprovalStatisticsCard from './components/ApprovalStatisticsCard';

const HRDocumentReviewApprovalCenter = () => {
  const { user } = useAuth();
  const { companySettings } = useCompanySettings();
  const companyName = companySettings?.companyName || 'HR Analytics';

  // State
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // UI State
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    category: null,
    department: null,
    priority: 'all'
  });

  // Categories
  const categories = [
    { id: 'all', label: 'All Documents', count: 0 },
    { id: 'contract', label: 'Contracts', count: 0 },
    { id: 'personal', label: 'Personal Documents', count: 0 },
    { id: 'compliance', label: 'Compliance Records', count: 0 },
    { id: 'employment_letter', label: 'Employment Letters', count: 0 }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [pendingDocuments, selectedCategory, searchTerm, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [docsData, statsData] = await Promise.all([
        hrDocumentApprovalService?.getPendingDocuments(),
        hrDocumentApprovalService?.getApprovalStatistics()
      ]);

      setPendingDocuments(docsData || []);
      setStatistics(statsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load pending documents');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...pendingDocuments];

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(doc => doc?.category === selectedCategory);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm?.toLowerCase();
      filtered = filtered?.filter(doc =>
        doc?.documentName?.toLowerCase()?.includes(term) ||
        doc?.employeeName?.toLowerCase()?.includes(term) ||
        doc?.employeeNumber?.toLowerCase()?.includes(term) ||
        doc?.department?.toLowerCase()?.includes(term)
      );
    }

    // Department filter
    if (filters?.department) {
      filtered = filtered?.filter(doc => doc?.department === filters?.department);
    }

    // Priority filter (based on submission date)
    if (filters?.priority === 'urgent') {
      const threeDaysAgo = new Date();
      threeDaysAgo?.setDate(threeDaysAgo?.getDate() - 3);
      filtered = filtered?.filter(doc => 
        new Date(doc?.submittedForApprovalAt) <= threeDaysAgo
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleReviewDocument = async (document) => {
    try {
      const details = await hrDocumentApprovalService?.getDocumentDetails(document?.id);
      setSelectedDocument(details);
      setShowReviewModal(true);
    } catch (err) {
      setError(err?.message || 'Failed to load document details');
    }
  };

  const handleApprovalSuccess = () => {
    setShowReviewModal(false);
    setSelectedDocument(null);
    setSuccess('Document processed successfully');
    loadData();
    setTimeout(() => setSuccess(''), 3000);
  };

  const getDaysWaiting = (submittedDate) => {
    if (!submittedDate) return 0;
    const now = new Date();
    const submitted = new Date(submittedDate);
    const diffTime = Math.abs(now - submitted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPriorityLevel = (daysWaiting) => {
    if (daysWaiting >= 5) return 'critical';
    if (daysWaiting >= 3) return 'high';
    if (daysWaiting >= 1) return 'medium';
    return 'low';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileCheck className="w-8 h-8 text-emerald-600" />
                HR Document Review & Approval Center
              </h1>
              <p className="text-gray-600 mt-2">
                Review and approve employee documents with digital signature capabilities
              </p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start gap-2">
            <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg flex items-start gap-2">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <ApprovalStatisticsCard
              title="Pending Review"
              value={statistics?.pending}
              icon={Clock}
              color="amber"
            />
            <ApprovalStatisticsCard
              title="Approved"
              value={statistics?.approved}
              icon={CheckCircle}
              color="emerald"
            />
            <ApprovalStatisticsCard
              title="Rejected"
              value={statistics?.rejected}
              icon={XCircle}
              color="red"
            />
            <ApprovalStatisticsCard
              title="Total Documents"
              value={statistics?.total}
              icon={FileText}
              color="blue"
            />
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by document name, employee, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {categories?.map(cat => (
                <option key={cat?.id} value={cat?.id}>
                  {cat?.label}
                </option>
              ))}
            </select>

            {/* Priority Filter */}
            <select
              value={filters?.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e?.target?.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">All Priority</option>
              <option value="urgent">Urgent (3+ days)</option>
            </select>
          </div>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : filteredDocuments?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <FileCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Documents</h3>
            <p className="text-gray-600">All documents have been reviewed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDocuments?.map(document => {
              const daysWaiting = getDaysWaiting(document?.submittedForApprovalAt);
              const priority = getPriorityLevel(daysWaiting);
              
              return (
                <DocumentApprovalCard
                  key={document?.id}
                  document={document}
                  daysWaiting={daysWaiting}
                  priority={priority}
                  onReview={() => handleReviewDocument(document)}
                />
              );
            })}
          </div>
        )}
      </main>

      {/* Review Modal */}
      {showReviewModal && selectedDocument && (
        <DocumentReviewModal
          document={selectedDocument}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedDocument(null);
          }}
          onSuccess={handleApprovalSuccess}
        />
      )}
    </div>
  );
};

export default HRDocumentReviewApprovalCenter;